import { useContext, useEffect, useRef } from 'react'
import {
  GlobalDataContext,
  TemplateContext,
  View,
  dataLocationToPath,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * ReactiveJSON primitive — the interactive SHELL of a site's main menu.
 *
 * It renders NOTHING of its own: entries, panels, columns and links are plain
 * YAML inside `content`, so they are ordinary rows of a `Switch` and therefore
 * inline-editable (cells, row rail, drag & drop) exactly like any other list.
 * The shell owns only what YAML cannot express — and nothing else:
 *
 *   - **Escape** and **click/focus outside** close the menu. A reactive-json
 *     `actions:` condition (`when`/`is`) is evaluated at RENDER time, never
 *     against the event, so "close only on Escape" has no declarative form.
 *   - **`aria-expanded`** must flip both ways. `setAttributeValue` writes the
 *     attribute in an effect with no cleanup: once set it never comes back, so
 *     a declarative toggle would stick on "true".
 *
 * Everything else IS declarative and stays in the YAML: which panel shows
 * (`actions: [{what: hide, when: ~~.navOpen, isNot: ~.label}]`), the classes,
 * the markup, the editable cells.
 *
 * The open entry's key is published to the reactive-json store at `statePath`
 * (default `~~.navOpen`) — a path OUTSIDE `~~.apiData`, so the block's
 * `DataSync` never persists a transient hover state.
 *
 * YAML usage:
 *   - type: NavShell
 *     statePath: ~~.navOpen          # optional
 *     content:
 *       - type: nav
 *         content:
 *           - type: Switch           # the entries — `data-nav-key: ~.label`
 *             content: ~~.…navItems  #   on each trigger is the whole contract
 *             singleOption: { load: navEntryTpl }
 *       - type: Switch               # the panels, hidden by a `hide` action
 *         content: ~~.…navItems
 *         singleOption: { load: navPanelTpl }
 *
 * Contract with the markup: any element carrying `data-nav-key` is a trigger.
 * Pointing at it (or focusing it) opens its panel, clicking it toggles — which
 * is also the touch path, where there is no hover.
 */
export const NavShell = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const ref = useRef(null)

  const statePath = props.statePath ?? '~~.navOpen'
  const openKey =
    evaluateTemplateValue({
      valueToEvaluate: statePath,
      globalDataContext,
      templateContext,
    }) ?? ''

  // The listeners below are bound once; they read the live value through this
  // ref instead of being rebound on every hover.
  const openRef = useRef(openKey)
  openRef.current = openKey

  const setOpen = useRef(null)
  setOpen.current = (key) => {
    if (openRef.current === key) return
    openRef.current = key
    globalDataContext?.updateData(
      key,
      dataLocationToPath({
        currentPath: templateContext.templatePath,
        dataLocation: statePath,
        globalDataContext,
        templateContext,
      }),
    )
  }

  useEffect(() => {
    const shell = ref.current
    if (!shell) return

    const triggerKey = (target) => {
      if (!(target instanceof Element)) return undefined
      const trigger = target.closest('[data-nav-key]')
      return trigger && shell.contains(trigger) ? trigger.getAttribute('data-nav-key') : undefined
    }
    const inside = (target) => target instanceof Node && shell.contains(target)

    // In the builder, a panel that vanished while a cell was being edited would
    // unmount the contentEditable and lose what was typed: the menu stays open
    // as long as it holds the field being edited.
    const isEditing = () => shell.querySelector('[data-builder-editing]') != null

    // ONE pointer listener does both halves of the hover behaviour: entering a
    // trigger opens it, leaving the menu altogether closes it. No grace delay
    // is needed — moving from a trigger down into its panel never leaves the
    // shell, which is their common ancestor.
    const onPointerOver = (e) => {
      const key = triggerKey(e.target)
      if (key !== undefined) { setOpen.current(key); return }
      if (!inside(e.target) && !isEditing()) setOpen.current('')
    }
    const onClick = (e) => {
      const key = triggerKey(e.target)
      if (key === undefined) {
        if (!inside(e.target)) setOpen.current('')
        return
      }
      // Touch has no hover: the tap itself must open, and tapping the open
      // entry again closes it.
      setOpen.current(openRef.current === key ? '' : key)
    }
    const onFocusIn = (e) => {
      const key = triggerKey(e.target)
      if (key !== undefined) { setOpen.current(key); return }
      if (!inside(e.target) && !isEditing()) setOpen.current('')
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && openRef.current !== '') {
        setOpen.current('')
        shell.querySelector(`[data-nav-key="${CSS.escape(openRef.current)}"]`)?.focus()
      }
    }

    document.addEventListener('pointerover', onPointerOver)
    document.addEventListener('click', onClick, true)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerover', onPointerOver)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // `aria-expanded` is driven here, both ways, over whatever the YAML rendered.
  useEffect(() => {
    const shell = ref.current
    if (!shell) return
    shell.querySelectorAll('[data-nav-key]').forEach((el) => {
      if (el.getAttribute('data-nav-panel') === 'none') return
      el.setAttribute('aria-expanded', el.getAttribute('data-nav-key') === openKey ? 'true' : 'false')
    })
  })

  return (
    <div ref={ref} style={{ display: 'contents' }}>
      <View
        props={props.content}
        path={path + '.content'}
        datafield="content"
        currentData={currentData?.content}
      />
    </div>
  )
}
